/**
 * 术语源（C-167 A7）。
 *
 * ## 为什么落在代码里，不落库
 *
 * 判断依据是**这份内容跟谁一起变**：
 *
 * | 如果落库 | 如果落代码 |
 *  |---|---|
 * | 改一条定义要走接口、要有编辑界面、要迁移 | 改一条定义就是改一行，跟着 PR 走 review |
 * | 客户可以改我们的术语定义 | 客户改不了——**这正是要的**：术语是产品定义，不是客户数据 |
 * | 术语与它解释的那段代码分处两地，改代码时不会想到改术语 | 同一个仓、同一次提交，改 `atc.cls` 的人看得见「运营 / 建设」这条 |
 *
 * 决定性的是最后一条。术语与判据编号绑在一起（每条都带出处），
 * 而判据编号变了是**代码变更**，不是数据变更。
 * 落库会造出一个跟不上代码的解释层——而一份过期的术语表比没有更坏。
 *
 * ## 出处只在这一页出现（C-162）
 *
 * 每条都有 `source`。**产品其他页面一律不显示判据编号**——
 * 编号是给我们自己对账的，不是给客户看的。所以这份数据只被本页与小 i 组件消费，
 * 不进任何业务页面的正文。
 *
 * ## 收录范围
 *
 * **凡非行业或世界共识的名词。** 部门、岗位、状态这类通用词不收——
 * 收了就是把一份词典变成一份废话表，人看两条就不看了。
 */

export type TermKind = 'own' | 'metric';

export type Term = {
  /** 名词本身。小 i 组件按这个字段查。 */
  term: string;
  /** 一句话定义。**一句**——写成三句就没人读了。 */
  definition: string;
  /** 计算口径：分子分母各是什么。只有 kind='metric' 的有。 */
  formula?: { numerator: string; denominator: string; note?: string };
  /** 出处：ADR 或规范条。**只在本页出现。** */
  source: string;
  kind: TermKind;
  /** 最容易被搞错的一点。没有就不写——不为对称硬凑。 */
  pitfall?: string;
};

export const TERMS: readonly Term[] = [
  {
    term: 'ATC',
    definition: '一张任务卡：一件有明确产出、可判断做完没做完的活。资产树的第三层，也是最小的资产单位。',
    source: 'ADR-0009（四段描述）、ADR-0008（确定性）、I-1（资产三层）',
    kind: 'own',
    pitfall: 'ATC 数是「有多少种活」，不是「干了多少次」。干了多少次是 TaskRun。',
  },
  {
    term: 'TaskRun',
    definition: '一张 ATC 的一次执行。仅追加、不可改、不进资产树。',
    source: 'ADR-0002、I-5、C-08',
    kind: 'own',
    pitfall: '一张卡一天可以有二十条 TaskRun。财务处理发票就是这样——一张卡、一天二十次。',
  },
  {
    term: '岗位空间',
    definition: '一个岗位的运行容器：这个岗位的卡、人、记录都归到这里。',
    source: '规范 4.1；ADR-0009 / ADR-0014（替代被禁用的「工作台」一词）',
    kind: 'own',
    pitfall: '它不是给员工操作的界面。中枢不做工作台（ADR-0005、B-1）。',
  },
  {
    term: 'ACTION 包',
    definition: '一张卡的某一版实际拿去跑的那套动作实现。锚在版本上，不锚在卡上。',
    source: 'C-167 A3；规范 6.3 第 5 页签；ADR-0049 五',
    kind: 'own',
    pitfall:
      '锚在卡上会让去年那条执行记录指向今年才换上的包。V0 卡没有包，空是合法的。',
  },
  {
    term: '数据胶囊',
    definition: '一个客户的数据边界：进得去、出不来，跨客户不混。',
    source: '规范 15.1；G-1 至 G-6',
    kind: 'own',
  },
  {
    term: '卡位',
    definition: '一个岗位上「一张卡该在的位置」。已经立了卡的是实的，还没立的是空的。',
    source: 'C-161 A2-1、A4；ADR-0001（一个房间＝一个 ATC）',
    kind: 'own',
    pitfall: '空卡位不是缺陷，是待建存量——它是蓝图的主体。',
  },
  {
    term: '卡位总数',
    definition: '这个岗位规划应有多少张卡。人填的调研结论，不由任何东西推算。',
    source: 'C-161 A2-1、口径一',
    kind: 'own',
    pitfall:
      '不得由人数推算。「5 人 = 40 间」那种算法已作废。空 = 还没调研；0 = 调研过、结论是不需要卡。',
  },
  {
    term: '空卡位',
    definition: '卡位总数减去已立卡数，库内算出来的。',
    source: 'C-161 A2-1（不得为前端常量）',
    kind: 'own',
    pitfall: '卡位总数没填时不画空位——那时连差几间都还不知道，不是差 0 间。',
  },
  {
    term: '成熟度 L0–L5',
    definition: '这张卡被自动化到什么程度：从全靠人做，到人只看结果。',
    source: 'ADR-0008；ATC 本质表',
    kind: 'own',
    pitfall: '成熟度是卡的属性，不是灯。灯是运行状态，来自 TaskRun。',
  },
  {
    term: '须审批',
    definition: '这张卡跑完要人点一下才算过（hotl gate）。',
    source: 'ADR-0008；规范 6.2',
    kind: 'own',
    pitfall: '探索性的卡不开须审批就上不了线——既无机器判据又无人工审批的卡不该上线。',
  },
  {
    term: '确定性 / 探索性',
    definition:
      '确定性：同样的输入必得同样的结论，机器判得出对错。探索性：结论要人判断。',
    source: 'ADR-0008；C-06',
    kind: 'own',
    pitfall:
      '探索性的活仍然是一张 ATC。进不进得了「定义认证」只看四段与断言、不看 determinism；进不进得了「在役」另有一道闸——探索性须开须审批。',
  },
  {
    term: '运营 ATC / 建设 ATC',
    definition: '运营卡跑客户的业务；建设卡把中枢本身建起来。',
    source: 'C-161；`atc.cls`',
    kind: 'own',
    pitfall:
      '这与「客户 / 我方」（`owner_scope`，ADR-0007）是两根轴，不是一根。把两者压成一根就会接错字段。',
  },
  {
    term: '共享岗',
    definition: '一个部门下供全员共用的岗位，用来承载「这个部门人人都要做的那些活」。',
    source: 'ADR-0020 一、三；`position.is_system` + `system_kind`',
    kind: 'own',
    pitfall: '共享范围靠系统岗承载，不靠挂载层级表达——挂法变了不等于范围变了。',
  },
  {
    term: '系统部门',
    definition: '系统建的部门（企业运营部 / 企业建设部），不可删、不可改名、不可改归属。',
    source: 'ADR-0020；`department.is_system`',
    kind: 'own',
  },
  {
    term: 'AI 替代率',
    definition: '当日的工时里，AI 完成的占多少。',
    formula: {
      numerator: 'AI 完成工时（当日）',
      denominator: '岗位编制工时 = 编制人数 × 标准工作日小时数',
      note: '分母缺编制或缺业务日历时显示「待编制数据到位」，不显示 0——算不出来不是 0。',
    },
    source: 'ADR-0047；MF-02；`v_position_ai_substitution_daily`',
    kind: 'metric',
    pitfall: '「替代率 0%」读作「AI 完全没起作用」，与「这个数还算不出来」含义相反。',
  },
  {
    term: '达成率',
    definition: '当天承诺要交的那些事，实际交成了几件。',
    formula: {
      numerator: 'matched（承诺项里实际交付的条数）',
      denominator: 'total（承诺项总条数）',
      note: '由库生成列算出，不得人工填写；未结算时为 null，不是 0。',
    },
    source: '规范 9.2；`daily_commitment`；视图 `v_enterprise_commitment_daily`',
    kind: 'metric',
    pitfall:
      '只有企业级。`daily_commitment` 只带 hub_id，部门级与岗位级无数据源（P-15），故那两级一个视图都不建。',
  },
  {
    term: '基线人工耗时',
    definition: '改造前，人做一次这件事要多少分钟。',
    formula: {
      numerator: '—',
      denominator: '—',
      note: '单值，不是比率。建卡那一刻的快照，随卡终身，是改造成效的分母。',
    },
    source: 'C-161 A2-2；`atc.baseline_duration_min`',
    kind: 'metric',
    pitfall:
      '它落在 `atc` 上而不是 `atc_version` 上——升版改的是四段定义，不能把分母一起改掉。',
  },
  {
    term: '实际均耗',
    definition: '改造后，实际跑一次平均多少分钟。',
    formula: { numerator: 'taskrun 时长之和', denominator: 'taskrun 条数' },
    source: '`v_atc_avg_duration`；C-161 A2-2',
    kind: 'metric',
    pitfall: '与基线人工耗时同名不同源：一个是调研录进来的事实，一个是跑出来的实测。',
  },
  {
    term: '基线日总耗时',
    definition: '这件事一天总共占多少人工时间（改造前）。建设排序按它降序。',
    formula: {
      numerator: '基线单次耗时 × 归一日均频次',
      denominator: '—',
      note: '西瓜按日总耗时量，不按单次时长量（C-161 口径三）。',
    },
    source: 'C-161 口径三；`v_atc_baseline_daily_minutes`',
    kind: 'metric',
    pitfall:
      '单次 20 分钟 × 每日 600 次 = 每日 200 小时。按单次时长排序会把这张卡排到末位，而它恰是最大的西瓜。',
  },
  {
    term: 'mapping 覆盖率',
    definition: '已经建成卡的那些活，占这个组织总工时的多少——建设进度，不是运营效果。',
    formula: {
      numerator: 'Σ 已建卡的基线日总耗时',
      denominator: '编制总工时',
    },
    source: 'C-161 A2-3、A2-4；`v_hub_mapping_coverage`',
    kind: 'metric',
    pitfall:
      '它与 AI 替代率、释放工时、达成率四个数不得互相顶替（C-161 A2-5）。建设进度 ≠ 运营效果。',
  },
  {
    term: '校验状态',
    definition:
      '这张统计表的数，有没有从过程表重算过一遍、核对过对不对。三档：未核 / 已核一致 / 已核不一致。',
    source: 'C-168 A3、A5、A6 第 6 段；`stat_table.last_verified_at` 与 `stat_verification`',
    kind: 'own',
    pitfall:
      '统计表不是 SSOT——SSOT 只在过程表那一层成立：一次记录、不可修改。只要去统计就有可能出错，' +
      '所以一个统计数可不可信，第一眼看的是这里，不是数值本身。「未核」不等于「一致」，' +
      '它是「还没人核过」。',
  },
  {
    term: '释放工时',
    definition: '压缩出来的、可以再投到别处去的时间。',
    formula: {
      numerator: '（基线日总耗时 − 实际日总耗时）',
      denominator: '—',
      note: '本轮未建，登记在册（C-161 A2-5）。',
    },
    source: 'C-161 A2-5、A2-6；关联 E-03 价值码计量口径',
    kind: 'metric',
    pitfall:
      '说「释放工时」，不说「节省人力 / 减员 / 替代人力」（C-161 A2-6）。压缩出来的是可再投入的时间资源，不是裁掉的编制——对经营者是完全不同的两句话。',
  },
  {
    /*
      这一条收的不是一个名词，是一处**有意的不一致**。
      不写明，它在两张台账上看起来就只是个笔误——而看起来像笔误的东西，
      下一个人会「顺手修好」，把两个不同的意思合并成一个。
    */
    term: 'draft（待补全 / 待上线）',
    definition:
      '同一个英文状态值 draft，在 ATC 台账上念「待补全」，在岗位台账上念「待上线」——因为它们指的不是同一件事：ATC 的 draft 是四段与 TEST 没写完，岗位的 draft 是四项（责任 / 权限边界 / 胜任标准 / 岗位空间）没齐。',
    source: 'C-169 裁定五；`atc_status_check`、`position_active_requires_triplet`',
    kind: 'own',
    pitfall:
      '三张资产表的其余状态值，同一个英文必须是同一个中文（在役 / 已停用 / 已退役…）。draft 是唯一的例外，且它是有意的——用同一个中文反而是错的。',
  },
];

/** 小 i 组件按名词查。查不到返回 undefined，不返回一个空壳——空壳会让人以为有解释。 */
export function termOf(name: string): Term | undefined {
  return TERMS.find((entry) => entry.term === name);
}
